import { createAsyncThunk } from '@reduxjs/toolkit';
import { authRepository } from 'container/authRepository';
import { AdminThunk } from 'reduxStore/appStore';
import {
  redirectToClientsList,
  redirectToLogin,
  redirectToProjectsList,
} from 'reduxStore/auth/utils';
import { fetchCurrentPulseProject } from 'reduxStore/project/pulseProject/actions';
import { serializeError } from 'shared/utils/redux';
import { AuthApi } from 'api/AuthApi';
import { UserUnauthorizedException } from 'exception/UserUnauthorizedException';
import { UserRoleType } from 'model/UserRoleType';

import { STORE_NAME } from './initialState';

export const fetchLoggedUser = createAsyncThunk(
  `${STORE_NAME}/fetchLoggedUser`,
  () => {
    return authRepository.logged();
  },
  { serializeError },
);

// Guards a single auto-retry of the bootstrap user fetch when the in-flight
// request is aborted (e.g. by a back/forward navigation race). Without the
// retry, the auth resource is stuck in `rejected` and the SPA renders blank
// until the user manually refreshes — see L1S-1295 QA feedback.
let hasRetriedBootstrap = false;

export const resetBootstrapRetryGuard = (): void => {
  hasRetriedBootstrap = false;
};

export const fetchAndProcessLoggedUser = createAsyncThunk(
  `${STORE_NAME}/fetchAndProcessLoggedUser`,
  (_, { dispatch }) => {
    dispatch(fetchLoggedUser())
      .unwrap()
      .then((loggedUser) => {
        if (loggedUser.roles.includes(UserRoleType.ClientAdmin)) {
          dispatch(fetchCurrentPulseProject());
        } else if (
          loggedUser.roles.includes(UserRoleType.Admin) ||
          loggedUser.roles.includes(UserRoleType.CircleAdmin)
        ) {
          redirectToClientsList();
        } else {
          redirectToProjectsList();
        }
      })
      .catch((error) => {
        if (error instanceof UserUnauthorizedException) {
          redirectToLogin();
          return;
        }
        if (!hasRetriedBootstrap) {
          hasRetriedBootstrap = true;
          dispatch(fetchAndProcessLoggedUser());
        }
      });
  },
  { serializeError },
);

export const resetPasswordRequest =
  ({ email, currentCircle }: AuthApi.ResetPasswordRequestParams): AdminThunk<Promise<unknown>> =>
  () =>
    authRepository.resetPasswordRequest({ email, currentCircle });

export const logout = (): AdminThunk<Promise<void>> => () =>
  authRepository.logout().finally(() => redirectToLogin());
